// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — hallInterior.js   ·   THE HALL ARCHITECT (OW-42)
// ────────────────────────────────────────────────────────────────────
// caP'n (2026-09-16): "I do want the player to be able to build their
// own interior and customize it … I did want to sell pre made layouts
// to save time, but we can do both?" — and on layouts: "it is to
// restructure the entire inside of the House of deeds with different
// walls, rooms, etc, so the player doesn't have to build it out
// themselves if they dont want to." Friends can copy a Hall.
//
// So the inside of the Hall is DATA: walls, doorways, paint and where
// each deed stands. This file owns that data and every rule about it.
// ⚑ NO REACT, AND NO KNOWLEDGE OF THE REGISTRY. The Hall's fixed shape
// (its map, deeds, fixtures) is handed in as `base` by PierHome, so this
// file runs in node and every rule here is tested for real.
//
// ── FIREBASE ────────────────────────────────────────────────────────
//   homes/{uid}/minerium/interior = {
//     v: 1,
//     walls: { "x,y": wallFinishId | 1 },   1 = the Hall's worn wall
//     doors: { "x,y": wallFinishId | 1 },   a doorway: a wall you walk through
//     floors:{ "x,y": floorFinishId },      paint over the worn floor
//     deeds: { achievementId: "x,y" },      where the player moved a deed
//     glow:  1?                             a risen layout: the walls breathe
//     from:  layoutItemId?                  the layout it was drawn from
//   }
//   homes/{uid}/minerium/designs/{slot} = { name, at, interior }
//     slots: "a" "b" "c" (the player's), "_last" (put it back), "copied"
//
// ── THE LAWS ────────────────────────────────────────────────────────
//   · Walls and doorways only go on the Hall's own FLOOR, never on a
//     fixture (the way in, the way out, the Deed Book, a frame's stand).
//   · THE HALL STAYS OPEN. Every floor tile you can reach before a wall
//     goes up, you can reach after it (bar the wall's own tile). Same
//     before/after law as `hallStaysOpen` for furniture.
//   · A deed is never lost and never sealed in. One that a wall lands on
//     SLIDES to the nearest free tile it can be reached on, preferring
//     its own wing — deterministically, so everyone sees the same Hall.
//   · A painting on the floor stands on an easel; on the plain north
//     wall it hangs. Medals and plinths stand on floor only.
//   · BUILDING COSTS ✹1 A TILE (caP'n, 2026-09-16: "building anything
//     costs 1 dog, removing/editing is free"). A wall or a doorway where
//     there was neither is a build; knocking down, turning a wall into a
//     doorway, painting, moving a deed and undo are edits, and free.
//     Paint is only the finishes you own; a layout brings its own paint.
//   · Composition NEVER THROWS and never white-screens: a bad key is
//     skipped, and a stored interior that would seal a fixture off is
//     drawn as the plain Hall (`broken: true`, its walls listed as
//     `hiddenWalls` so the tools can show and erase them); nothing is
//     deleted.
//   · `base` MUST BE ONE STABLE OBJECT (PierHome's getHallBase()). The
//     composition is cached per base and returned by identity, because
//     Hall3D is React.memo'd. A composed Hall is SHARED — never write
//     into its map, sets or deeds.
//   · A row from a newer game (v > INTERIOR_V) is drawn, never written.
// ════════════════════════════════════════════════════════════════════

import { ref, get, update, set, serverTimestamp } from "firebase/database";
import { db, auth } from "./firebase";
import { ITEMS, KIND, AREAS, LAYER, layerOf } from "./mineriumCatalogue";
import { spendDawgs, refundDawgs } from "./upDawgs";
import {
  holdingPath, normaliseHolding, dimsOf, tilesUnder, blockedByProp, resolvePlacement, pickUpProp,
  growthOf, serverNow, LIFT_TTL_MS,
} from "./minerium";

export const INTERIOR_V = 1;
export const DESIGN_SLOTS = ["a", "b", "c"];
export const LAST_SLOT = "_last";
export const COPIED_SLOT = "copied";
const ALL_SLOTS = new Set([...DESIGN_SLOTS, LAST_SLOT, COPIED_SLOT]);
export const DESIGN_NAME_MAX = 24;

const interiorPath = (u) => `${holdingPath(u)}/interior`;
const designsPath  = (u) => `${holdingPath(u)}/designs`;
const ownedPath    = (u) => `profiles/${u}/minerium/owned`;

export const tileKey = (x, y) => `${x},${y}`;
const KEY_RE = /^(\d{1,3}),(\d{1,3})$/;
export function parseKey(k) {
  const m = typeof k === "string" && KEY_RE.exec(k);
  return m ? [Number(m[1]), Number(m[2])] : null;
}
const isWallFinish  = (id) => ITEMS[id]?.kind === KIND.HALL_WALL;
const isFloorFinish = (id) => ITEMS[id]?.kind === KIND.HALL_FLOOR;
const isLayout      = (id) => ITEMS[id]?.kind === KIND.HALL_LAYOUT;

// ════════════════════════════════════════════════════════════════════
// §1 · SCHEMA
// ════════════════════════════════════════════════════════════════════
// ⚑ A normalised interior carries this mark, so the hot path (compose,
// checks during a drag) never re-filters one. Symbols do not reach JSON.
const NORM = Symbol("hallInterior.normalised");

export function emptyInterior() {
  return { v: INTERIOR_V, walls: {}, doors: {}, floors: {}, deeds: {}, glow: false, from: null, future: false, [NORM]: true };
}

// ⚠ Filtered on the way in, like every holding row: a retired finish
// becomes the worn one (1), a bad key is skipped, a tile that is both a
// wall and a doorway is a doorway. Nothing here ever throws.
export function normaliseInterior(raw) {
  if (raw && raw[NORM]) return raw;
  const out = emptyInterior();
  if (!raw || typeof raw !== "object") return out;
  const obj = (o) => (o && typeof o === "object" ? o : {});
  const wallVal = (v) => (isWallFinish(v) ? v : 1);
  for (const [k, v] of Object.entries(obj(raw.walls))) if (parseKey(k) && v) out.walls[k] = wallVal(v);
  for (const [k, v] of Object.entries(obj(raw.doors))) {
    if (!parseKey(k) || !v) continue;
    out.doors[k] = wallVal(v);
    delete out.walls[k];
  }
  for (const [k, v] of Object.entries(obj(raw.floors))) if (parseKey(k) && isFloorFinish(v)) out.floors[k] = v;
  for (const [id, k] of Object.entries(obj(raw.deeds))) if (parseKey(k)) out.deeds[id] = k;
  out.glow = !!raw.glow;
  out.from = typeof raw.from === "string" && isLayout(raw.from) ? raw.from : null;
  out.future = Number(raw.v) > INTERIOR_V;
  return out;
}

// an edited copy that is still normalised (the maps are ours to change)
const edit = (n) => ({ ...n, walls: { ...n.walls }, doors: { ...n.doors }, floors: { ...n.floors }, deeds: { ...n.deeds } });

export function isEmptyInterior(i) {
  const n = normaliseInterior(i);
  return !Object.keys(n.walls).length && !Object.keys(n.doors).length &&
    !Object.keys(n.floors).length && !Object.keys(n.deeds).length;
}

// what goes to Firebase: no empty maps (RTDB drops them anyway), no nulls
function toRow(i) {
  const n = normaliseInterior(i);
  const row = { v: INTERIOR_V };
  for (const f of ["walls", "doors", "floors", "deeds"]) if (Object.keys(n[f]).length) row[f] = n[f];
  if (n.glow) row.glow = 1;
  if (n.from) row.from = n.from;
  return row;
}

// a stable fingerprint — memo keys, texture keys
export function interiorKey(i) {
  const n = normaliseInterior(i);
  const part = (o) => Object.keys(o).sort().map((k) => `${k}=${o[k]}`).join(";");
  return `w:${part(n.walls)}|d:${part(n.doors)}|f:${part(n.floors)}|m:${part(n.deeds)}|g:${n.glow ? 1 : 0}|o:${n.from || ""}|v:${n.future ? "F" : ""}`;
}

// ── WHAT PAINT YOU MAY USE ────────────────────────────────────────────
// A finish you own, a free one, or one that a layout you own is painted
// with (a layout brings its own paint — that is part of what it sells).
// The breathing walls come with owning a risen layout.
// ⚑ cached by WHICH layouts are owned, never by the object: a listener's
// map is the same object before and after a purchase lands in it.
const _kit = new Map();
function layoutKit(owned) {
  const ids = owned && typeof owned === "object" ? Object.keys(owned).filter((id) => owned[id] && isLayout(id)).sort() : [];
  const key = ids.join("|");
  let k = _kit.get(key);
  if (k) return k;
  k = { paint: new Set(), glow: false };
  for (const id of ids) {
    const plan = planOf(id);
    if (plan?.wall) k.paint.add(plan.wall);
    for (const f of Object.values(plan?.paint || {})) k.paint.add(f);
    if (ITEMS[id].ascendedFrom) k.glow = true;
  }
  _kit.set(key, k);
  return k;
}
export const ownsFinish = (owned, id) => !!ITEMS[id] && (ITEMS[id].priceUD === 0 || !!owned?.[id]);
export const finishAllowed = (owned, id) => ownsFinish(owned, id) || layoutKit(owned).paint.has(id);
export const glowAllowed = (owned) => layoutKit(owned).glow;

// an interior with only what `owned` allows — every apply goes through this
export function sanitiseInterior(interior, owned, { keepDeeds = true } = {}) {
  const n = normaliseInterior(interior);
  const out = emptyInterior();
  for (const [k, v] of Object.entries(n.walls)) out.walls[k] = v !== 1 && finishAllowed(owned, v) ? v : 1;
  for (const [k, v] of Object.entries(n.doors)) out.doors[k] = v !== 1 && finishAllowed(owned, v) ? v : 1;
  for (const [k, v] of Object.entries(n.floors)) if (finishAllowed(owned, v)) out.floors[k] = v;
  if (keepDeeds) out.deeds = { ...n.deeds };
  out.glow = n.glow && glowAllowed(owned);
  out.from = n.from && owned?.[n.from] ? n.from : null;
  return out;
}

// ════════════════════════════════════════════════════════════════════
// §2 · THE BASE — the Hall as it is built, before anybody touches it
// ────────────────────────────────────────────────────────────────────
// base = {
//   map, cols, rows, T,           the fixed map (T.FLOOR/WALL/VOID/WALLTROPHY/FRAME)
//   trophies,                     [{ id, kind, x, y, wing, … }] default spots
//   fixtures: Map "x,y" → reason  tiles nothing may be built on (way in/out,
//                                 Deed Book, frame stands). ⚠ NOT deed tiles:
//                                 deeds move; `composed.reserved` adds them.
//   spawn, wallRows, floorTop, floorBot,
//   wings: [{ game, x0, x1, label }], gutters: [x…]  (between wings)
// }
// ════════════════════════════════════════════════════════════════════
const inFloorBand = (base, x, y) => y >= base.floorTop && y <= base.floorBot && x >= 1 && x < base.cols - 1;
export const baseFloor = (base, x, y) => Number.isInteger(x) && Number.isInteger(y) && inFloorBand(base, x, y) && base.map[y]?.[x] === base.T.FLOOR;
const wingOf = (base, x) => base.wings.find((w) => x >= w.x0 && x <= w.x1)?.game || null;

// ⭐ one generic flood, from the doorway in
export function reachableFrom(open, start) {
  const seen = new Set();
  if (!start || !open(start.x, start.y)) return seen;
  seen.add(tileKey(start.x, start.y));
  const q = [[start.x, start.y]];
  for (let i = 0; i < q.length; i++) {
    const [x, y] = q[i];
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      const nx = x + dx, ny = y + dy, k = tileKey(nx, ny);
      if (seen.has(k) || !open(nx, ny)) continue;
      seen.add(k); q.push([nx, ny]);
    }
  }
  return seen;
}

// ════════════════════════════════════════════════════════════════════
// §3 · COMPOSE — base + interior → the Hall everyone sees
// ────────────────────────────────────────────────────────────────────
// ⚑ An LRU per base: a drag checks a dozen "what if" Halls, and the one
// on screen must never be evicted by them (it is re-touched every render).
// ════════════════════════════════════════════════════════════════════
const COMPOSE_KEEP = 48;
const _composeCache = new WeakMap();   // base → Map(key → composed)
const _pinned = new WeakMap();         // base → the key on screen (never evicted)
// ⭐ the Hall on screen is PINNED: a drag's what-ifs can fill the cache, and
// evicting the shown Hall would hand React a new object on the next snapshot
export function pinHall(base, composed) { if (composed?.key) _pinned.set(base, composed.key.replace(/#plain$/, "")); }
const _deedObjs = new WeakMap();       // base → Map(id → last deed object)
const _deedArrs = new WeakMap();       // base → the last deeds array

// `peek` — a what-if: cached, but never allowed to replace the deed objects
// the Hall on screen hands to React
export function composeHall(base, interior, { peek = false } = {}) {
  const n = normaliseInterior(interior);
  const key = interiorKey(n);
  let per = _composeCache.get(base);
  if (!per) { per = new Map(); _composeCache.set(base, per); }
  const hit = per.get(key);
  if (hit) { per.delete(key); per.set(key, hit); return hit; }
  let out = composeUncached(base, n, key, false, peek);
  if (out.broken) {
    const plain = composeUncached(base, { ...emptyInterior(), floors: n.floors, deeds: n.deeds }, key, true, peek);
    out = { ...plain, broken: true, hiddenWalls: out.hiddenWalls, hiddenDoors: out.hiddenDoors };
  }
  per.set(key, out);
  const pin = _pinned.get(base);
  for (const k of per.keys()) {
    if (per.size <= COMPOSE_KEEP) break;
    if (k !== pin) per.delete(k);
  }
  return out;
}

function composeUncached(base, n, key, plain, peek = false) {
  const { T, cols } = base;
  const map = base.map.map((row) => row.slice());
  const walls = [], doors = [];
  const wallAt = new Map(), doorAt = new Map();
  const buildable = (x, y) => baseFloor(base, x, y) && !base.fixtures.has(tileKey(x, y));
  for (const [k, fin] of Object.entries(n.walls)) {
    const p = parseKey(k); if (!p || !buildable(p[0], p[1])) continue;
    map[p[1]][p[0]] = T.WALL; wallAt.set(k, fin);
  }
  for (const [k, fin] of Object.entries(n.doors)) {
    const p = parseKey(k); if (!p || !buildable(p[0], p[1])) continue;
    doorAt.set(k, fin);
  }
  for (const [k, fin] of wallAt) { const [x, y] = parseKey(k); walls.push({ x, y, finish: fin }); }

  const walk = (x, y) => map[y]?.[x] === T.FLOOR;
  const reach = reachableFrom(walk, base.spawn);
  // a fixture that can no longer be reached is a Hall nobody can use
  if (!plain) {
    for (const k of base.fixtures.keys()) {
      const [x, y] = parseKey(k);
      if (walk(x, y) && !reach.has(k)) {
        return { broken: true, hiddenWalls: walls, hiddenDoors: [...doorAt].map(([dk, fin]) => { const [dx, dy] = parseKey(dk); return { x: dx, y: dy, finish: fin }; }) };
      }
    }
  }
  // the north wall is plain again until a painting is hung on it below
  for (const r of base.wallRows) for (let x = 0; x < cols; x++) if (map[r][x] === T.WALLTROPHY) map[r][x] = T.WALL;

  const wallish = (x, y) => {
    const c = map[y]?.[x];
    return c === T.WALL || c === T.WALLTROPHY || c === T.FRAME || doorAt.has(tileKey(x, y));
  };
  for (const [k, fin] of doorAt) {
    const [x, y] = parseKey(k);
    // the wall runs east–west when its neighbours are east and west, and
    // you then walk through it north–south
    const ew = wallish(x - 1, y) && wallish(x + 1, y);
    const ns = wallish(x, y - 1) && wallish(x, y + 1);
    // a doorway at the END of a run takes that run's direction
    const runs = ew ? "ew" : ns ? "ns"
      : (wallish(x, y - 1) || wallish(x, y + 1)) && !(wallish(x - 1, y) || wallish(x + 1, y)) ? "ns" : "ew";
    const through = runs === "ew" ? walk(x, y - 1) && walk(x, y + 1) : walk(x - 1, y) && walk(x + 1, y);
    doors.push({ x, y, finish: fin, runs, lone: !ew && !ns, blind: !through });
  }

  // ── THE DEEDS ──────────────────────────────────────────────────────
  // numeric grids: this runs for every "what if" a drag asks
  const idx = (x, y) => y * cols + x;
  const reachG = new Uint8Array(cols * base.rows);
  for (const k of reach) { const [x, y] = parseKey(k); reachG[idx(x, y)] = 1; }
  const takenG = new Uint8Array(cols * base.rows);
  const fixedG = new Uint8Array(cols * base.rows);
  for (const k of base.fixtures.keys()) { const [x, y] = parseKey(k); fixedG[idx(x, y)] = 1; }
  const doorG = new Uint8Array(cols * base.rows);
  for (const k of doorAt.keys()) { const [x, y] = parseKey(k); doorG[idx(x, y)] = 1; }
  const inGrid = (x, y) => x >= 0 && y >= 0 && x < cols && y < base.rows;
  const wallSpotFree = (x, y) =>
    base.wallRows.includes(y) && !!wingOf(base, x) && map[y][x] === T.WALL && base.map[y][x] !== T.FRAME &&
    !takenG[idx(x, y)] && reachG[idx(x, y + 1)] === 1 && !doorG[idx(x, y + 1)];
  const floorFree = (x, y) => inGrid(x, y) && inFloorBand(base, x, y) &&
    reachG[idx(x, y)] === 1 && !fixedG[idx(x, y)] && !doorG[idx(x, y)] && !takenG[idx(x, y)];
  const hangs = (t, y) => t.kind === "painting" && base.wallRows.includes(y);
  const okFor = (t, x, y) => (inGrid(x, y) && t.kind === "painting" && wallSpotFree(x, y)) || floorFree(x, y);

  let objs = _deedObjs.get(base);
  if (!objs) { objs = new Map(); _deedObjs.set(base, objs); }
  const make = (t, x, y, mount, moved, slid, stuck) => {
    const prev = objs.get(t.id);
    if (prev && prev.x === x && prev.y === y && prev.mount === mount && prev.moved === moved && prev.slid === slid && prev.stuck === stuck) return prev;
    const o = { ...t, x, y, home: { x: t.x, y: t.y }, mount, moved, slid, stuck };
    if (!peek) objs.set(t.id, o);
    return o;
  };
  function place(t, x, y, moved, slid) {
    takenG[idx(x, y)] = 1;
    const onWall = hangs(t, y);
    if (onWall) map[y][x] = T.WALLTROPHY;
    return make(t, x, y, onWall ? "wall" : t.kind === "painting" ? "easel" : "floor", moved, slid, false);
  }
  // ⚑ outward in rings from where it wanted to be: its own wing first, then
  // anywhere. A hung painting prefers the wall by two steps over an easel.
  const maxD = cols + base.rows;
  function nearest(t, wx, wy) {
    const wantsWall = hangs(t, wy);
    const wing = wingOf(base, wx);
    for (const only of wing ? [wing, null] : [null]) {
      const good = (x) => !only || wingOf(base, x) === only;
      for (let d = 0; d <= maxD; d++) {
        const rings = wantsWall ? [[d, true], [d - 2, false]] : [[d, false]];
        for (const [e, wallOnly] of rings) {
          if (e < 0) continue;
          for (let dy = -e; dy <= e; dy++) {
            const rem = e - Math.abs(dy);
            for (const dx of rem === 0 ? [0] : [-rem, rem]) {
              const x = wx + dx, y = wy + dy;
              if (!good(x) || !inGrid(x, y)) continue;
              if (wallOnly ? wallSpotFree(x, y) : floorFree(x, y)) return [x, y];
            }
          }
        }
      }
    }
    return null;
  }

  let trophies = new Array(base.trophies.length);
  const pending = [];
  // player-moved deeds claim their tile first, then the defaults, in order
  const moved = [], rest = [];
  base.trophies.forEach((t, i) => (n.deeds[t.id] ? moved : rest).push(i));
  for (const i of [...moved, ...rest]) {
    const t = base.trophies[i];
    const want = parseKey(n.deeds[t.id]) || [t.x, t.y];
    if (okFor(t, want[0], want[1])) trophies[i] = place(t, want[0], want[1], !!n.deeds[t.id], false);
    else pending.push([t, i, want]);
  }
  for (const [t, i, want] of pending) {
    const best = nearest(t, want[0], want[1]);
    if (best) { trophies[i] = place(t, best[0], best[1], !!n.deeds[t.id], true); continue; }
    // ⛑ never dropped: with nowhere free it stays home and is marked STUCK
    // (the tools refuse any change that makes a deed stuck)
    takenG[idx(t.x, t.y)] = 1;
    trophies[i] = make(t, t.x, t.y, hangs(t, t.y) ? "wall" : "floor", false, true, true);
  }

  // ⚑ the same deeds in the same places → the same array (FloorDeeds keys on it)
  const lastArr = _deedArrs.get(base);
  if (lastArr && lastArr.length === trophies.length && lastArr.every((o, i) => o === trophies[i])) trophies = lastArr;
  else if (!peek) _deedArrs.set(base, trophies);

  // ── WHAT NOTHING ELSE MAY STAND ON ────────────────────────────────
  const reserved = new Map(base.fixtures);
  const deedAt = new Map();
  for (const t of trophies) {
    if (t.mount === "wall") continue;
    deedAt.set(tileKey(t.x, t.y), t.id);
    if (walk(t.x, t.y)) reserved.set(tileKey(t.x, t.y), "A deed stands there.");
  }
  for (const d of doors) reserved.set(tileKey(d.x, d.y), "Keep the doorway clear.");

  // ── THE BUSTS IN THE NICHES — a bay's empty north wall (hallBuild) ──
  const wallDeedXs = trophies.filter((t) => t.mount === "wall").map((t) => t.x);
  const nicheXs = [];
  for (const w of base.wings) {
    for (let x = w.x0 + 1; x <= w.x1 - 1; x += 2) {
      if (wallDeedXs.some((dx) => Math.abs(dx - x) <= 1)) continue;
      if (!walk(x, base.floorTop) || doorAt.has(tileKey(x, base.floorTop))) continue;
      nicheXs.push(x);
    }
  }

  // ── PAINT ─────────────────────────────────────────────────────────
  const floors = [];
  for (const [k, fin] of Object.entries(n.floors)) {
    const p = parseKey(k);
    if (p && baseFloor(base, p[0], p[1]) && !wallAt.has(k)) floors.push({ x: p[0], y: p[1], finish: fin });
  }
  const group = (list) => {
    const g = {};
    for (const c of list) (g[c.finish] ||= []).push(c);
    return g;
  };

  return {
    key: plain ? `${key}#plain` : key, broken: false, hiddenWalls: [], hiddenDoors: [],
    map, walls, doors, floors,
    wallsByFinish: group(walls), doorsByFinish: group(doors), floorsByFinish: group(floors),
    wallAt, doorAt, deedAt, glow: !!n.glow, from: n.from, future: !!n.future,
    trophies, reserved, reach, nicheXs,
    stuck: trophies.filter((t) => t.stuck).length,
    isWalkable: (x, y) => x >= 0 && y >= 0 && x < cols && y < base.rows && map[y][x] === T.FLOOR,
  };
}

// ⭐ WHICH WALLS SHUT A BROKEN HALL — the fewest to knock down, found
// greedily from the shut-off fixture outward (walls nearest it first)
export function sealingWalls(base, interior) {
  const n = normaliseInterior(interior);
  if (!composeHall(base, n, { peek: true }).broken) return [];
  const plain = composeHall(base, { ...emptyInterior(), deeds: n.deeds }, { peek: true });
  const trial = composeUncached(base, n, "sealing", true, true);
  const shut = [...base.fixtures.keys()].map(parseKey).filter(([x, y]) => plain.isWalkable(x, y) && !trial.reach.has(tileKey(x, y)));
  const dist = (k) => { const [x, y] = parseKey(k); return Math.min(...shut.map(([fx, fy]) => Math.abs(fx - x) + Math.abs(fy - y))); };
  const keys = [...Object.keys(n.walls)].sort((a, b) => dist(a) - dist(b) || (a < b ? -1 : 1));
  const next = edit(n), out = [];
  let open = false;
  for (const k of keys) {
    delete next.walls[k]; out.push(k);
    if (!composeHall(base, next, { peek: true }).broken) { open = true; break; }
  }
  if (!open) return out.map(parseKey);
  // then put back every one the Hall does not actually need gone
  const keep = [];
  for (const k of out) {
    next.walls[k] = n.walls[k];
    if (composeHall(base, next, { peek: true }).broken) { delete next.walls[k]; keep.push(k); }
  }
  return keep.map(parseKey);
}

// ════════════════════════════════════════════════════════════════════
// §4 · VALIDATION — true, or the reason a player reads
// ════════════════════════════════════════════════════════════════════
export const TOOL = { WALL: "wall", DOOR: "door", ERASE: "erase", FLOOR: "floor", PAINT: "paint" };
const FUTURE = "This Hall was built with a newer version of the game — update to change it.";

const hallProps = (props) => {
  const out = {};
  for (const [id, p] of Object.entries(props || {})) if (p && p.area === AREAS.HALL) out[id] = p;
  return out;
};
// every tile a hall piece covers, with the piece (memoised per props bag)
const _tiles = new WeakMap();
export function propTiles(props) {
  if (props && _tiles.has(props)) return _tiles.get(props);
  const m = new Map();
  const hp = hallProps(props);
  for (const p of Object.values(hp)) {
    const at = p.on ? resolvePlacement(hp, p.propId) || p : p;
    for (const [x, y] of tilesUnder(at.x, at.y, dimsOf(p.itemId, p.rot))) {
      const k = tileKey(x, y);
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(p);
    }
  }
  if (props) _tiles.set(props, m);
  return m;
}
const pieceName = (p) => ITEMS[p?.itemId]?.name || "furniture";
const standing = (list) => (list || []).filter((p) => layerOf(p.itemId) !== LAYER.FLOOR);

// reach with furniture standing in it — what a player can actually walk
const _reachWith = new WeakMap();   // composed → WeakMap(props → Set)
function reachWith(composed, base, props) {
  const bag = props || {};
  let per = _reachWith.get(composed);
  if (!per) { per = new WeakMap(); _reachWith.set(composed, per); }
  if (per.has(bag)) return per.get(bag);
  const hp = hallProps(bag);
  const r = reachableFrom((x, y) => composed.isWalkable(x, y) && !blockedByProp(hp, AREAS.HALL, x, y, 0.3), base.spawn);
  per.set(bag, r);
  return r;
}

// Would changing ONE tile be allowed? `owned` = { itemId: truthy } for paint.
export function interiorCellOk(base, interior, props, tool, x, y, finish = null, owned = null) {
  const n = normaliseInterior(interior);
  if (n.future) return FUTURE;
  const k = tileKey(x, y);
  if (!baseFloor(base, x, y)) return "That is outside the Hall's floor.";
  if (tool === TOOL.FLOOR) {
    if (finish && !isFloorFinish(finish)) return "That is not a floor.";
    if (finish && !finishAllowed(owned, finish)) return "You do not own that floor yet.";
    if (n.walls[k]) return "There is a wall there.";
    return true;
  }
  if (finish && (tool === TOOL.PAINT || tool === TOOL.WALL || tool === TOOL.DOOR)) {
    if (!isWallFinish(finish)) return "That is not a wall finish.";
    if (!finishAllowed(owned, finish)) return "You do not own that finish yet.";
  }
  if (tool === TOOL.PAINT) return n.walls[k] || n.doors[k] ? true : "Paint goes on a wall you built.";
  if (tool === TOOL.ERASE) return n.walls[k] || n.doors[k] ? true : "There is nothing built there.";
  if (tool !== TOOL.WALL && tool !== TOOL.DOOR) return "Pick a tool first.";

  const before = composeHall(base, n);
  if (before.broken) return "Part of your Hall is shut off — erase one of the faded walls first.";
  const fixed = base.fixtures.get(k);
  if (fixed) return fixed;
  if (tool === TOOL.WALL && n.walls[k]) return "There is already a wall there.";
  if (tool === TOOL.DOOR && n.doors[k]) return "There is already a doorway there.";
  const here = propTiles(props).get(k) || [];
  const pieces = tool === TOOL.WALL ? here : standing(here);
  if (pieces.length) return `Your ${pieceName(pieces[0])} is in the way.`;

  const next = edit(n);
  if (tool === TOOL.WALL) { next.walls[k] = n.doors[k] || finish || 1; delete next.doors[k]; }
  else { next.doors[k] = n.walls[k] || finish || 1; delete next.walls[k]; }
  const after = composeHall(base, next, { peek: true });
  if (after.broken) return "That would wall off part of your Hall — leave a doorway.";
  if (tool === TOOL.DOOR) {
    const d = after.doors.find((dd) => dd.x === x && dd.y === y);
    if (d?.lone) return "A doorway goes in a wall — build wall either side of it first.";
    if (d?.blind) return "A doorway has to lead somewhere — clear both sides of it.";
  } else {
    // ⚑ THE HALL STAYS OPEN — everything reachable before is reachable after
    const was = reachWith(before, base, props);
    const now = reachWith(after, base, props);
    for (const t of was) if (t !== k && !now.has(t)) return sealWhy(base, t);
    // …and no doorway beside it is bricked up
    const wasBlind = new Set(before.doors.filter((d) => d.blind).map((d) => tileKey(d.x, d.y)));
    if (after.doors.some((d) => d.blind && !wasBlind.has(tileKey(d.x, d.y)))) return "That would brick up a doorway.";
  }
  if (after.stuck > before.stuck) return "A deed would have nowhere to go.";
  // a deed that has to slide must not slide under furniture
  const onPiece = deedOnFurniture(after, before, props);
  if (onPiece) return `A deed would have to move onto your ${pieceName(onPiece)} — move it first.`;
  return true;
}

function sealWhy(base, k) {
  const [x] = parseKey(k);
  const w = base.wings.find((ww) => x >= ww.x0 && x <= ww.x1);
  return w ? `That would wall off part of the ${w.label} wing — leave a doorway.` : "That would wall off part of your Hall — leave a doorway.";
}

// a floor deed that is now on a tile it was not on before, under furniture
function deedOnFurniture(after, before, props) {
  const pt = propTiles(props);
  for (let i = 0; i < after.trophies.length; i++) {
    const t = after.trophies[i], b = before?.trophies[i];
    if (t.mount === "wall" || (b && b.x === t.x && b.y === t.y)) continue;
    const hit = standing(pt.get(tileKey(t.x, t.y)))[0];
    if (hit) return hit;
  }
  return null;
}

// Where may this deed go? true, or the reason
// ⚑ Deeds are walked over (PierHome's walk loop never tests them), so a
// deed never blocks a way — only walls and furniture do.
export function deedSpotOk(base, interior, props, deedId, x, y) {
  const n = normaliseInterior(interior);
  if (n.future) return FUTURE;
  const c = composeHall(base, n);
  const me = c.trophies.find((t) => t.id === deedId);
  if (!me) return "That deed is not in your Hall.";
  if (!Number.isInteger(x) || !Number.isInteger(y)) return "Put it on a whole tile.";
  if (me.x === x && me.y === y) return "It is already there.";
  const k = tileKey(x, y);
  if (base.wallRows.includes(y)) {
    if (me.kind !== "painting") return "Only a painting hangs on the wall.";
    if (!wingOf(base, x) || c.map[y]?.[x] !== base.T.WALL || base.map[y]?.[x] === base.T.FRAME) return "Hang it on a plain stretch of the north wall, inside a bay.";
    const below = tileKey(x, y + 1);
    if (!c.reach.has(below) || c.doorAt.has(below)) return "Nobody could stand in front of it there.";
    const hung = (propTiles(props).get(below) || []).find((p) => layerOf(p.itemId) === LAYER.WALL);
    if (hung) return `Your ${pieceName(hung)} hangs there.`;
  } else {
    if (!baseFloor(base, x, y) || !c.isWalkable(x, y)) return "That is through a wall.";
    if (!c.reach.has(k)) return "Nobody could reach it there.";
    const fixed = base.fixtures.get(k);
    if (fixed) return fixed;
    if (c.doorAt.has(k)) return "Keep the doorway clear.";
    const pieces = standing(propTiles(props).get(k));
    if (pieces.length) return `Your ${pieceName(pieces[0])} is in the way.`;
  }
  if (c.trophies.some((t) => t.id !== deedId && t.x === x && t.y === y)) return "Another deed is there.";
  // a painting leaving the wall opens a niche — never behind a hanging
  if (me.mount === "wall") {
    const next = edit(n); next.deeds[deedId] = k;
    const after = composeHall(base, next, { peek: true });
    const fresh = after.nicheXs.filter((nx) => !c.nicheXs.includes(nx));
    const hung = Object.values(hallProps(props)).find((p) => layerOf(p.itemId) === LAYER.WALL &&
      tilesUnder(p.x, p.y, dimsOf(p.itemId, p.rot)).some(([hx, hy]) => hy === base.floorTop && fresh.includes(hx)));
    if (hung) return `A bust would stand behind your ${pieceName(hung)} — move it first.`;
  }
  return true;
}

// ════════════════════════════════════════════════════════════════════
// §5 · WRITES — the owner only
// ────────────────────────────────────────────────────────────────────
// ⚑ `owned` for paint comes from the caller's live listener. Paint is
// cosmetic in your own Hall and the rules cannot price-check it anyway;
// every WHOLE-HALL apply reads ownership fresh.
// ════════════════════════════════════════════════════════════════════
const notYou = () => ({ ok: false, reason: "not_you", message: "You can only build in your own Hall." });
const isMe = (uid) => !!uid && auth.currentUser?.uid === uid;
const failed = (message = "It would not settle. Try again.") => ({ ok: false, reason: "write_failed", message });

// ⭐ One stroke of a tool over one or more tiles (a drag). Each tile is
// checked against the Hall AS THE STROKE HAS LEFT IT, so a drag can draw
// a wall and then its doorway. Tiles that fail are skipped and reported;
// the rest land in ONE update.
export const BUILD_COST_UD = 1;

export async function buildCells(uid, base, interior, props, tool, cells, { finish = null, owned = null, charge = false } = {}) {
  if (!isMe(uid)) return notYou();
  let cur = normaliseInterior(interior);
  if (cur.future) return { ok: false, reason: "future", message: FUTURE };
  const patch = {};
  const skipped = [];
  let fresh = 0;                       // tiles that had neither wall nor doorway: builds
  for (const [x, y] of cells || []) {
    const why = interiorCellOk(base, cur, props, tool, x, y, finish, owned);
    if (why !== true) { skipped.push({ x, y, why }); continue; }
    const k = tileKey(x, y);
    if ((tool === TOOL.WALL || tool === TOOL.DOOR) && !cur.walls[k] && !cur.doors[k]) fresh++;
    cur = edit(cur);
    if (tool === TOOL.WALL) {
      cur.walls[k] = finish || cur.doors[k] || 1; delete cur.doors[k];
      patch[`walls/${k}`] = cur.walls[k]; patch[`doors/${k}`] = null;
    } else if (tool === TOOL.DOOR) {
      cur.doors[k] = finish || cur.walls[k] || 1; delete cur.walls[k];
      patch[`doors/${k}`] = cur.doors[k]; patch[`walls/${k}`] = null;
    } else if (tool === TOOL.ERASE) {
      delete cur.walls[k]; delete cur.doors[k];
      patch[`walls/${k}`] = null; patch[`doors/${k}`] = null;
    } else if (tool === TOOL.FLOOR) {
      if (finish) cur.floors[k] = finish; else delete cur.floors[k];
      patch[`floors/${k}`] = finish || null;
    } else if (tool === TOOL.PAINT) {
      const v = finish || 1;
      if (cur.walls[k]) { cur.walls[k] = v; patch[`walls/${k}`] = v; }
      else { cur.doors[k] = v; patch[`doors/${k}`] = v; }
    }
  }
  if (!Object.keys(patch).length) {
    return { ok: false, reason: "refused", message: skipped[0]?.why || "Nothing to build there.", skipped };
  }
  const changed = Object.keys(patch).length;
  patch.v = INTERIOR_V;
  // the moment you change the walls it is your Hall, not the layout's
  if (tool === TOOL.WALL || tool === TOOL.DOOR || tool === TOOL.ERASE) { patch.from = null; cur.from = null; }
  // ⭐ THE PRICE, taken BEFORE the stone goes up and given back if it does not
  const cost = charge ? fresh * BUILD_COST_UD : 0;
  let paid = 0;
  if (cost > 0) {
    paid = await spendDawgs(uid, cost).catch(() => 0);
    if (paid !== cost) {
      return { ok: false, reason: "no_funds", skipped,
               message: `That costs ✹${cost} (✹${BUILD_COST_UD} a tile) — not enough Dawgs.` };
    }
  }
  try { await update(ref(db, interiorPath(uid)), patch); }
  catch {
    if (paid) await refundDawgs(uid, paid, "hall_build_failed").catch(() => {});
    return { ...failed(), skipped };
  }
  return { ok: true, interior: cur, skipped, changed, paid };
}

export async function moveDeed(uid, base, interior, props, deedId, x, y) {
  if (!isMe(uid)) return notYou();
  const why = deedSpotOk(base, interior, props, deedId, x, y);
  if (why !== true) return { ok: false, reason: "refused", message: why };
  const home = base.trophies.find((t) => t.id === deedId);
  const back = !!home && home.x === x && home.y === y;
  try { await update(ref(db, `${interiorPath(uid)}/deeds`), { [deedId]: back ? null : tileKey(x, y) }); }
  catch { return failed(); }
  return { ok: true, deedId, x, y, home: back };
}

// every deed back where the Hall first put it — unless one would land
// under furniture, which is named instead
export async function resetDeeds(uid, base, interior, props) {
  if (!isMe(uid)) return notYou();
  const n = normaliseInterior(interior);
  if (n.future) return { ok: false, reason: "future", message: FUTURE };
  const next = edit(n); next.deeds = {};
  const before = composeHall(base, n), after = composeHall(base, next, { peek: true });
  const hit = deedOnFurniture(after, before, props);
  if (hit) return { ok: false, reason: "refused", message: `A deed's home is under your ${pieceName(hit)} — move it first.` };
  try { await set(ref(db, `${interiorPath(uid)}/deeds`), null); }
  catch { return failed(); }
  return { ok: true };
}

// ════════════════════════════════════════════════════════════════════
// §6 · APPLYING A WHOLE INTERIOR — a layout, a saved design, put-it-back
// ────────────────────────────────────────────────────────────────────
// ⚑ ORDER: read the holding FRESH → check every piece that must move can
// move → send them back to the shed → write the new interior AND the
// put-it-back copy in ONE update. Nothing is written until every lift has
// landed, so a refusal leaves the Hall and PUT IT BACK as they were.
// What is "in the way": any piece on a new wall; a standing or hung piece
// on a doorway or where a deed now stands; a hanging PierHome says has lost
// its wall; and solid pieces that would seal part of the Hall.
// ════════════════════════════════════════════════════════════════════
export function inTheWay(base, interior, props, hangOk = null) {
  const c = composeHall(base, interior, { peek: true });
  const hp = hallProps(props);
  const out = new Set();
  for (const [k, list] of propTiles(hp)) {
    for (const piece of list) {
      if (c.wallAt.has(k) || !c.isWalkable(...parseKey(k))) out.add(piece.propId);
      else if (layerOf(piece.itemId) !== LAYER.FLOOR && (c.doorAt.has(k) || c.deedAt.has(k))) out.add(piece.propId);
    }
  }
  if (hangOk) for (const p of Object.values(hp)) if (layerOf(p.itemId) === LAYER.WALL && !out.has(p.propId) && hangOk(c, p) !== true) out.add(p.propId);
  const withKids = () => {
    let grew = true;
    while (grew) {
      grew = false;
      for (const p of Object.values(hp)) if (p.on && out.has(p.on) && !out.has(p.propId)) { out.add(p.propId); grew = true; }
    }
  };
  withKids();
  // then, while part of the Hall is sealed, lift the ONE piece on the edge
  // of the open floor whose going opens the most of it
  const solid = (p) => !p.on && ITEMS[p.itemId] && !ITEMS[p.itemId].walkOver;
  const left = () => { const o = {}; for (const [id, p] of Object.entries(hp)) if (!out.has(id)) o[id] = p; return o; };
  for (let guard = 0; guard < 60; guard++) {
    const rest = left();
    const r = reachWith(c, base, rest);
    const sealed = [];
    for (const k of c.reach) if (!r.has(k) && !blockedByProp(rest, AREAS.HALL, ...parseKey(k), 0.3)) sealed.push(k);
    if (!sealed.length) break;
    const edge = Object.values(rest).filter((p) => solid(p) && tilesUnder(p.x, p.y, dimsOf(p.itemId, p.rot))
      .some(([x, y]) => [[1, 0], [-1, 0], [0, 1], [0, -1]].some(([dx, dy]) => r.has(tileKey(x + dx, y + dy)))))
      .sort((a, b) => (a.propId < b.propId ? -1 : 1));
    let pick = null, most = 0;
    for (const p of edge) {
      const without = { ...rest }; delete without[p.propId];
      const r2 = reachableFrom((x, y) => c.isWalkable(x, y) && !blockedByProp(without, AREAS.HALL, x, y, 0.3), base.spawn);
      const opened = sealed.filter((k) => r2.has(k)).length;
      if (opened > most) { most = opened; pick = p; }
    }
    // two pieces side by side: neither alone opens anything — take the edge piece nearest the sealed floor
    if (!pick) {
      let pd = Infinity;
      for (const p of edge) for (const k of sealed) {
        const [sx, sy] = parseKey(k);
        const d = Math.abs(p.x - sx) + Math.abs(p.y - sy);
        if (d < pd) { pd = d; pick = p; }
      }
    }
    if (!pick) break;
    out.add(pick.propId);
    withKids();
  }
  return [...out];
}

async function freshState(uid) {
  const [h, o] = await Promise.all([get(ref(db, holdingPath(uid))), get(ref(db, ownedPath(uid)))]);
  const holding = normaliseHolding(h.val());
  return { holding, props: holding.props, current: normaliseInterior(holding.interiorRaw), owned: o.val() || {} };
}

// can this piece go to the shed right now? true, or why not
async function canLift(uid, p) {
  if (p.lifting && serverNow() - Number(p.lifting) < LIFT_TTL_MS) return "somebody is already lifting it";
  if (ITEMS[p.itemId]?.kind === KIND.GROWER) {
    const g = growthOf(ITEMS[p.itemId], (await get(ref(db, `${holdingPath(uid)}/growers/${p.propId}`))).val(), serverNow());
    if (g && g.ready > 0) return `collect the ✹${g.ready} in it first`;
  }
  return true;
}

// `build` = an interior, or (current, owned) → interior
export async function applyInterior(uid, base, build, { hangOk = null, remember = true, name = null, lastName = null } = {}) {
  if (!isMe(uid)) return notYou();
  let st;
  try { st = await freshState(uid); }
  catch { return { ok: false, reason: "read_failed", message: "Could not read your Hall. Try again." }; }
  if (st.current.future) return { ok: false, reason: "future", message: FUTURE };
  const raw = typeof build === "function" ? build(st.current, st.owned) : build;
  if (!raw) return { ok: false, reason: "nothing", message: "There is nothing to build." };
  if (normaliseInterior(raw).future) return { ok: false, reason: "future", message: FUTURE };
  const n = sanitiseInterior(raw, st.owned);
  // ⚠ a change that changes nothing must not overwrite PUT IT BACK (review)
  if (interiorKey(n) === interiorKey(st.current)) return { ok: false, reason: "same", message: "Your Hall is already exactly that." };
  if (composeHall(base, n, { peek: true }).broken) return { ok: false, reason: "sealed", message: "That design would shut the way through this Hall." };

  // the raw rows carry `lifting`; the normalised ones do not
  const rawProps = ((await get(ref(db, `${holdingPath(uid)}/props`)).catch(() => null))?.val()) || {};
  const lift = inTheWay(base, n, st.props, hangOk);
  for (const id of lift) {
    const why = await canLift(uid, { ...st.props[id], lifting: rawProps[id]?.lifting }).catch(() => "it could not be checked");
    if (why !== true) {
      return { ok: false, reason: "lift_refused", lifted: [],
               message: `Your ${pieceName(st.props[id])} is in the way and cannot go to the shed (${why}). Nothing was changed.` };
    }
  }
  let bag = { ...hallProps(st.props) };
  const lifted = [];
  const depth = (id) => { let d = 0, p = bag[id]; while (p?.on && d < 8) { d++; p = bag[p.on]; } return d; };
  lift.sort((a, b) => depth(b) - depth(a) || (a < b ? -1 : 1));   // a piece before whatever it stands on
  for (const id of lift) {
    if (!bag[id]) continue;
    const r = await pickUpProp(uid, id, bag);
    if (!r.ok) {
      return { ok: false, reason: "lift_refused", lifted,
               message: `Your ${pieceName(bag[id])} could not go to the shed (${r.message || r.reason}).` +
                 (lifted.length ? ` ${lifted.length} piece${lifted.length > 1 ? "s are" : " is"} already safe in your shed.` : "") +
                 " Nothing was rebuilt." };
    }
    lifted.push(bag[id].itemId);
    const nb = { ...bag }; delete nb[id]; bag = nb;
  }
  // ⚠ the lifts took round trips: if the Hall changed meanwhile (a deed moved
  // on another device), build on THAT, not on the first read
  let nn = n, current = st.current;
  try {
    const again = normaliseInterior((await get(ref(db, interiorPath(uid)))).val());
    if (interiorKey(again) !== interiorKey(st.current)) {
      const raw2 = typeof build === "function" ? build(again, st.owned) : build;
      const n2 = raw2 ? sanitiseInterior(raw2, st.owned) : null;
      if (n2 && !composeHall(base, n2, { peek: true }).broken) { nn = n2; current = again; }
    }
  } catch { /* the first read stands */ }
  const patch = { interior: toRow(nn) };
  // PUT IT BACK is named after what it would put back
  const was = lastName || (current.from && ITEMS[current.from]?.name) || "Your Hall as it was";
  if (remember) patch[`designs/${LAST_SLOT}`] = { name: String(was).slice(0, DESIGN_NAME_MAX), at: serverTimestamp(), interior: toRow(current) };
  try { await update(ref(db, holdingPath(uid)), patch); }
  catch { return { ...failed(), lifted }; }
  return { ok: true, lifted, name, interior: nn };
}

// ════════════════════════════════════════════════════════════════════
// §7 · DESIGNS — three slots, put-it-back, and a friend's Hall
// ════════════════════════════════════════════════════════════════════
export function normaliseDesigns(raw) {
  const out = {};
  for (const [slot, d] of Object.entries(raw && typeof raw === "object" ? raw : {})) {
    if (!ALL_SLOTS.has(slot) || !d || typeof d !== "object") continue;
    out[slot] = {
      slot,
      name: String(d.name || "").slice(0, DESIGN_NAME_MAX) || (slot === LAST_SLOT ? "Before the change" : "A design"),
      at: Number(d.at) || 0,
      by: typeof d.by === "string" ? d.by : null,
      interior: normaliseInterior(d.interior),
    };
  }
  return out;
}

export async function saveDesign(uid, slot, interior, name = "") {
  if (!isMe(uid)) return notYou();
  if (!DESIGN_SLOTS.includes(slot)) return { ok: false, reason: "bad_slot", message: "There is no such slot." };
  if (normaliseInterior(interior).future) return { ok: false, reason: "future", message: FUTURE };
  const nm = String(name || "").trim().slice(0, DESIGN_NAME_MAX) || `Design ${slot.toUpperCase()}`;
  try { await set(ref(db, `${designsPath(uid)}/${slot}`), { name: nm, at: serverTimestamp(), interior: toRow(interior) }); }
  catch { return failed("It would not save. Try again."); }
  return { ok: true, slot, name: nm };
}

export async function clearDesign(uid, slot) {
  if (!isMe(uid)) return notYou();
  if (!ALL_SLOTS.has(slot)) return { ok: false, reason: "bad_slot", message: "There is no such slot." };
  try { await set(ref(db, `${designsPath(uid)}/${slot}`), null); }
  catch { return failed("It would not clear. Try again."); }
  return { ok: true };
}

// ⭐ COPY THIS HALL. The structure — walls, doorways, and the paint you
// may use too — never the deeds (they are theirs) and never the furniture.
// Paint you may not use becomes the Hall's worn finish.
export const structureOf = (interior, owned = null) => sanitiseInterior(interior, owned, { keepDeeds: false });

export async function copyHallFrom(uid, ownerUid, ownerName = "") {
  if (!isMe(uid)) return notYou();
  if (!ownerUid || ownerUid === uid) return { ok: false, reason: "self", message: "This Hall is already yours." };
  let raw, owned;
  try {
    const [a, b] = await Promise.all([get(ref(db, interiorPath(ownerUid))), get(ref(db, ownedPath(uid)))]);
    raw = a.val(); owned = b.val() || {};
  } catch { return { ok: false, reason: "read_failed", message: "Could not read their Hall. Try again." }; }
  if (normaliseInterior(raw).future) return { ok: false, reason: "future", message: "Their Hall was built with a newer version of the game — update to copy it." };
  const s = structureOf(raw, owned);
  if (!Object.keys(s.walls).length && !Object.keys(s.doors).length && !Object.keys(s.floors).length) {
    return { ok: false, reason: "nothing", message: "Their Hall has nothing built in it yet." };
  }
  const name = `${String(ownerName || "A friend").slice(0, 16)}'s Hall`;
  try { await set(ref(db, `${designsPath(uid)}/${COPIED_SLOT}`), { name, at: serverTimestamp(), by: ownerUid, interior: toRow(s) }); }
  catch { return failed("It would not save. Try again."); }
  return { ok: true, slot: COPIED_SLOT, name };
}

// ════════════════════════════════════════════════════════════════════
// §8 · LAYOUTS — the pre-made interiors Ally sells
// ────────────────────────────────────────────────────────────────────
// A plan is drawn ONCE PER BAY and tiled across every wing, so a new
// game's wing gets every layout for free (the "one entry adds
// everything" law). Rows are the Hall's floor rows, north to south:
//   row 0  y3  the easel row          row 5  y8   free
//   row 1  y4  free                   row 6  y9   medal overflow
//   row 2  y5  plinths (first)        row 7  y10  medals (first)
//   row 3  y6  free                   row 8  y11  the frames' stands
//   row 4  y7  plinth overflow
//   '.' open  '#' wall  'D' doorway  a–z floor paint (plan.paint)
// `gutter` is the column BETWEEN two bays. The way in (x = 1) and the
// Hall's east end are never drawn on. Fixture tiles are skipped.
// ⚑ Layouts keep the busy deed rows (y5, y10) and the frame row open, so
// a new layout moves as few deeds as it can.
// ════════════════════════════════════════════════════════════════════
export const LAYOUT_PLANS = {
  enfilade: {
    wall: "wall_stone", paint: { a: "floor_slate" },
    bay: [
      "..........",
      "..........",
      "..........",
      "..........",
      "aaaaaaaaaa",
      "..........",
      "..........",
      "..........",
      "..........",
    ],
    gutter: "####D####",
    gutterPaint: "....a....",
  },
  twin_parlours: {
    wall: "wall_dusk", paint: { a: "floor_slate" },
    bay: [
      "aaaaaaaaaa",
      "aaaaaaaaaa",
      "aaaaaaaaaa",
      "##D####D##",
      "..........",
      "..........",
      "..........",
      "..........",
      "..........",
    ],
    gutter: "...#.....",
  },
  long_gallery: {
    wall: "wall_stone", paint: { a: "floor_slate" },
    bay: [
      "..........",
      "#D######D#",
      "aaaaaaaaaa",
      "aaaaaaaaaa",
      "aaaaaaaaaa",
      "#D######D#",
      "..........",
      "..........",
      "..........",
    ],
    gutter: ".#aaa#...",
  },
  cloister: {
    wall: "wall_dusk", paint: { g: "floor_anima" },
    bay: [
      "..........",
      "..##DD##..",
      "..#gggg#..",
      "..DggggD..",
      "..#gggg#..",
      "..##DD##..",
      "..........",
      "..........",
      "..........",
    ],
    gutter: "#.#.#.#.#",
  },
  chambered: {
    wall: "wall_stone", paint: { a: "floor_slate" },
    bay: [
      "aaaaaaaaaa",
      "aaaaaaaaaa",
      "aaaaaaaaaa",
      "###D##D###",
      "..........",
      "..........",
      "..........",
      "..........",
      "..........",
    ],
    gutter: "#D#####D#",
  },
  labyrinth: {
    wall: "wall_nullus", paint: { s: "floor_slate" },
    bay: [
      "..........",
      "#D####.###",
      "..........",
      "###.###D##",
      "#........#",
      "##D###.###",
      "#..#ss#..#",
      "..........",
      "..........",
    ],
    gutter: "#######D#",
  },
};
// a risen layout is its legendary's plan, with walls that breathe
export function planOf(layoutItemId, _depth = 0) {
  const it = ITEMS[layoutItemId];
  if (!it || it.kind !== KIND.HALL_LAYOUT || _depth > 2) return null;
  if (it.ascendedFrom) return planOf(it.ascendedFrom, _depth + 1);
  return LAYOUT_PLANS[it.layout] || null;
}

export function expandPlan(base, plan, { glow = false, from = null } = {}) {
  const out = emptyInterior();
  if (!plan) return out;
  const put = (x, y, ch, paintCh) => {
    if (!baseFloor(base, x, y)) return;
    const k = tileKey(x, y);
    const paint = plan.paint?.[paintCh] || plan.paint?.[ch];
    // a fixture keeps its floor open, but takes the room's paint
    if (base.fixtures.has(k)) { if (paint) out.floors[k] = paint; return; }
    if (ch === "#") { out.walls[k] = plan.wall || 1; return; }
    if (ch === "D") out.doors[k] = plan.wall || 1;
    if (paint) out.floors[k] = paint;
  };
  for (const w of base.wings) {
    plan.bay.forEach((row, r) => {
      for (let i = 0; i < Math.min(row.length, w.x1 - w.x0 + 1); i++) put(w.x0 + i, base.floorTop + r, row[i]);
    });
  }
  for (const gx of base.gutters || []) {
    for (let r = 0; r < (plan.gutter || "").length; r++) put(gx, base.floorTop + r, plan.gutter[r], plan.gutterPaint?.[r]);
  }
  out.glow = !!glow;
  out.from = from;
  return out;
}

export function layoutInterior(base, layoutItemId, keepDeeds = null) {
  const plan = planOf(layoutItemId);
  if (!plan) return null;
  const i = expandPlan(base, plan, { glow: !!ITEMS[layoutItemId].ascendedFrom, from: layoutItemId });
  if (keepDeeds) i.deeds = { ...keepDeeds };
  return i;
}

// applies an owned layout: the deeds you moved stay moved
export async function applyLayout(uid, base, layoutItemId, opts = {}) {
  if (!isMe(uid)) return notYou();
  const it = ITEMS[layoutItemId];
  if (!it || it.kind !== KIND.HALL_LAYOUT || !planOf(layoutItemId)) return { ok: false, reason: "no_such_item", message: "That is not a layout." };
  let refused = null;
  const r = await applyInterior(uid, base, (cur, owned) => {
    if (!(it.priceUD === 0 || owned[layoutItemId])) { refused = true; return null; }
    return layoutInterior(base, layoutItemId, cur.deeds);
  }, { ...opts, name: it.name });
  if (refused) return { ok: false, reason: "not_owned", message: "You do not own that layout yet." };
  return r;
}

// a saved slot, the copied Hall, or PUT IT BACK (`_last`)
export async function applyDesign(uid, base, slot, designs, opts = {}) {
  if (!isMe(uid)) return notYou();
  const d = normaliseDesigns(designs)[slot];
  if (!d) return { ok: false, reason: "empty", message: "That slot is empty." };
  if (d.interior.future) return { ok: false, reason: "future", message: FUTURE };
  // putting it back SWAPS: what you put back becomes the next put-it-back
  // ⚑ the Copied Hall and PUT IT BACK are WALLS — the deeds stay where you
  // have them now; only your own saved designs carry deed positions
  return applyInterior(uid, base, (cur) => (slot === COPIED_SLOT || slot === LAST_SLOT ? { ...d.interior, deeds: cur.deeds } : d.interior), {
    ...opts, name: slot === LAST_SLOT ? "Put back" : d.name,
  });
}

// a clean Hall: no walls, no paint; deeds keep where you put them
export const plainInterior = (keepDeeds = null) => ({ ...emptyInterior(), deeds: { ...(keepDeeds || {}) } });
export const applyPlain = (uid, base, opts = {}) =>
  applyInterior(uid, base, (cur) => plainInterior(cur.deeds), { ...opts, name: "A plain Hall" });

const hallInteriorApi = {
  emptyInterior, normaliseInterior, sanitiseInterior, composeHall, interiorCellOk, deedSpotOk, buildCells,
  moveDeed, resetDeeds, inTheWay, applyInterior, saveDesign, clearDesign, copyHallFrom, structureOf,
  expandPlan, layoutInterior, applyLayout, applyDesign, applyPlain, planOf, normaliseDesigns,
};
export default hallInteriorApi;
